import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  toggleNotificationPreference,
  resetNotificationPreferences,
  clearApiMessage,
  clearError,
  selectNotificationPreferences,
  selectAuthLoading,
  selectAuthError,
  selectApiMessage,
  selectCurrentUser,
} from '../../store/slices/authSlice';
import {
  Mail,
  Smartphone,
  Bell,
  Cake,
  AlertTriangle,
  BarChart3,
  User,
  XCircle,
  Inbox,
  Check,
  CheckCircle2,
  Trash2,
  RefreshCw,
  Loader2,
  Save,
} from 'lucide-react';

const Notifications = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [notifList, setNotifList] = useState([]);
  const [filter, setFilter] = useState('All');
  const [selectedNotifs, setSelectedNotifs] = useState([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if component is mounted (to prevent showing messages on initial load)
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // ==================== SELECTORS ====================
  const notificationPreferences = useSelector(selectNotificationPreferences);
  const loading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);
  const apiMessage = useSelector(selectApiMessage);
  const currentUser = useSelector(selectCurrentUser);

  // ==================== NOTIFICATIONS STATE ====================
  // Using the same notification types as Settings component
  const [notifications, setNotifications] = useState([
    { id: 'email', label: 'Email Notifications', icon: Mail, desc: 'Receive notifications via email', on: true },
    { id: 'sms', label: 'SMS Notifications', icon: Smartphone, desc: 'Receive notifications via SMS', on: true },
    { id: 'push', label: 'Push Notifications', icon: Bell, desc: 'Receive browser push notifications', on: true },
    { id: 'birthday', label: 'Birthday Reminders', icon: Cake, desc: 'Get reminded about member birthdays', on: true },
    { id: 'low_credit', label: 'Low Credit Alerts', icon: AlertTriangle, desc: 'Alert when SMS credits are low', on: true },
    { id: 'weekly_digest', label: 'Weekly Digest', icon: BarChart3, desc: 'Receive weekly summary reports', on: false },
    { id: 'new_member', label: 'New Member Alerts', icon: User, desc: 'Get notified when new members join', on: true },
    { id: 'failed_sms', label: 'Failed SMS Alerts', icon: XCircle, desc: 'Alert when SMS delivery fails', on: false },
    { id: 'new_inbox', label: 'New Inbox Messages', icon: Inbox, desc: 'Notify when new messages arrive', on: true }
  ]);

  // ==================== MOCK NOTIFICATIONS ====================
  // These would come from a real API endpoint
  const mockNotifications = [
    { 
      id: 1, 
      title: 'New Member Registration', 
      desc: 'Emmanuel Osei has just registered as a member of Grace Chapel.',
      time: '2 minutes ago', 
      type: 'Member',
      icon: User,
      color: '#4f46e5',
      read: false
    },
    { 
      id: 2, 
      title: 'Birthday Reminder', 
      desc: 'Today is Sarah Akoto\'s birthday! 🎉 Don\'t forget to send a blessing.',
      time: '1 hour ago', 
      type: 'Birthday',
      icon: Cake,
      color: '#d97706',
      read: false
    },
    { 
      id: 3, 
      title: 'Low SMS Credits', 
      desc: 'You have only 250 SMS credits remaining. Please top up to avoid interruptions.',
      time: '3 hours ago', 
      type: 'Alert',
      icon: AlertTriangle,
      color: '#dc2626',
      read: false
    },
    { 
      id: 4, 
      title: 'New Inbox Message', 
      desc: 'You received a new message from the Youth Ministry regarding Sunday\'s event.',
      time: '5 hours ago', 
      type: 'Message',
      icon: Inbox,
      color: '#3b82f6',
      read: true
    },
    { 
      id: 5, 
      title: 'Weekly Digest', 
      desc: 'Your weekly report: 12 new members, 45 SMS sent, 8 prayer requests received.',
      time: 'Yesterday', 
      type: 'Report',
      icon: BarChart3,
      color: '#0891b2',
      read: true
    }
  ];

  // ==================== EFFECTS ====================

  // Load notification preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (currentUser) {
        try {
          setIsLoadingPreferences(true);
          const result = await dispatch(getNotificationPreferences()).unwrap();
          if (result.success && result.preferences) {
            // Update notifications with API data
            setNotifications(prev => prev.map(n => ({
              ...n,
              on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
            })));
          }
        } catch (error) {
          console.error('Failed to load notification preferences:', error);
        } finally {
          setIsLoadingPreferences(false);
        }
      }
    };
    
    loadPreferences();
    setNotifList(mockNotifications);
    
    return () => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    };
  }, [dispatch, currentUser]);

  // Set initial load to false after component mounts
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  // Display API messages - ONLY show when an action is taken (not on initial load)
  useEffect(() => {
    if (apiMessage && showToast && !isInitialLoad) {
      // Skip silent messages
      const silentMessages = [
        'Notification preferences retrieved',
        'Notification preferences retrieved successfully',
        'Preferences retrieved successfully'
      ];
      
      const shouldIgnore = silentMessages.some(silent => 
        apiMessage.toLowerCase().includes(silent.toLowerCase())
      );
      
      if (!shouldIgnore) {
        showToast(apiMessage, error ? 'error' : 'success');
      }
      
      // Always clear the message
      setTimeout(() => {
        dispatch(clearApiMessage());
        dispatch(clearError());
      }, 5000);
    }
  }, [apiMessage, error, dispatch, showToast, isInitialLoad]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== NOTIFICATION ACTIONS ====================

  // Mark all as read
  const markAllRead = () => {
    const unreadCount = notifList.filter(n => !n.read).length;
    if (unreadCount === 0) {
      showToastMessage('All notifications are already read', 'info');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Check size={16} style={{ verticalAlign: '-3px' }} /> Mark All as Read</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to mark all <strong>{unreadCount}</strong> unread notifications as read?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--blue-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--blue)' 
          }}>
            <CheckCircle2 size={16} style={{ verticalAlign: '-3px' }} /> This will clear the unread badge for all notifications.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-gold btn-sm"
            onClick={() => {
              setNotifList(notifList.map(n => ({ ...n, read: true })));
              setSelectedNotifs([]);
              showToastMessage('✅ All notifications marked as read!', 'success');
              closeModal();
            }}
          >
            <Check size={16} style={{ verticalAlign: '-3px' }} /> Mark All Read
          </button>
        </div>
      </div>
    );
  };

  // Mark single notification as read
  const markAsRead = (notification) => {
    setNotifList(notifList.map(n => 
      n.id === notification.id ? { ...n, read: true } : n
    ));
    showToastMessage(`📬 "${notification.title}" marked as read`, 'info');
  };

  // Delete notification
  const handleDeleteNotification = (notification) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Notification</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete this notification?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
              {notification.title}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '4px' }}>
              {notification.desc}
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              setNotifList(notifList.filter(n => n.id !== notification.id));
              showToastMessage(`🗑️ Notification deleted`, 'error');
              closeModal();
            }}
          >
            Delete Notification
          </button>
        </div>
      </div>
    );
  };

  // Bulk delete notifications
  const handleBulkDelete = () => {
    if (selectedNotifs.length === 0) {
      showToastMessage('Please select at least one notification', 'error');
      return;
    }

    const selectedItems = notifList.filter(n => selectedNotifs.includes(n.id));

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Bulk Delete</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{selectedNotifs.length}</strong> notifications?
          </p>
          <div style={{ 
            maxHeight: '150px', 
            overflowY: 'auto',
            marginBottom: '12px'
          }}>
            {selectedItems.map(n => (
              <div key={n.id} style={{ 
                padding: '6px 10px', 
                borderBottom: '1px solid var(--border-light)',
                fontSize: '13px'
              }}>
                {n.title}
              </div>
            ))}
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This action cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            onClick={() => {
              setNotifList(notifList.filter(n => !selectedNotifs.includes(n.id)));
              setSelectedNotifs([]);
              showToastMessage(`🗑️ ${selectedNotifs.length} notifications deleted`, 'error');
              closeModal();
            }}
          >
            Delete Selected
          </button>
        </div>
      </div>
    );
  };

  // View notification details
  const handleViewDetails = (notification) => {
    // Mark as read when viewed
    if (!notification.read) {
      setNotifList(notifList.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      ));
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Mail size={16} style={{ verticalAlign: '-3px' }} /> Notification Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            marginBottom: '16px',
            padding: '12px',
            background: 'var(--cream)',
            borderRadius: '8px'
          }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: '12px', 
              background: `${notification.color}18`, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontSize: '24px'
            }}>
              <notification.icon size={24} style={{ color: notification.color }} />
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>
                {notification.title}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {notification.time}
              </div>
            </div>
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>
              Message
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text)', lineHeight: '1.6' }}>
              {notification.desc}
            </div>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px 1fr', 
            gap: '8px', 
            fontSize: '13px' 
          }}>
            <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Type</span>
            <span>
              <span className="badge" style={{ 
                background: `${notification.color}18`, 
                color: notification.color,
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                {notification.type}
              </span>
            </span>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '120px 1fr', 
            gap: '8px', 
            fontSize: '13px',
            marginTop: '8px'
          }}>
            <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span>
            <span>
              <span className={`badge ${notification.read ? 'badge-green' : 'badge-amber'}`}>
                {notification.read ? 'Read' : 'Unread'}
              </span>
            </span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          {!notification.read && (
            <button className="btn btn-gold btn-sm" onClick={() => {
              markAsRead(notification);
              closeModal();
            }}>
              <Check size={16} style={{ verticalAlign: '-3px' }} /> Mark as Read
            </button>
          )}
        </div>
      </div>
    );
  };

  // ==================== NOTIFICATION PREFERENCES ====================

  // Toggle single notification preference
  const toggleNotification = async (id) => {
    // Find the notification
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;

    // Optimistically update UI
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, on: !n.on } : n
    ));

    try {
      const result = await dispatch(toggleNotificationPreference(id)).unwrap();
      if (result.success) {
        // Update with actual value from API
        setNotifications(prev => prev.map(n => 
          n.id === id ? { ...n, on: result.preference?.enabled ?? !n.on } : n
        ));
      }
    } catch (error) {
      // Revert on error
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, on: notification.on } : n
      ));
      showToastMessage(`Failed to update ${notification.label}`, 'error');
    }
  };

  // Save all notification preferences
  const handleSaveNotifications = async () => {
    dispatch(clearApiMessage());
    dispatch(clearError());

    setIsSubmitting(true);

    // Prepare data for API
    const preferencesData = {};
    notifications.forEach(n => {
      preferencesData[n.id] = n.on;
    });

    try {
      const result = await dispatch(updateNotificationPreferences(preferencesData)).unwrap();
      if (result.success) {
        // Update UI with API response
        if (result.preferences) {
          setNotifications(prev => prev.map(n => ({
            ...n,
            on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
          })));
        }
        // Message will be shown via apiMessage useEffect
      }
    } catch (error) {
      // Error will be shown via apiMessage useEffect
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset notification preferences
  const handleResetNotifications = async () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> Reset Notification Preferences</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to reset all notification preferences to defaults?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
<AlertTriangle size={16} style={{ verticalAlign: '-3px' }} /> This will reset all notification settings to their default values.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm"
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(resetNotificationPreferences()).unwrap();
                if (result.success && result.preferences) {
                  setNotifications(prev => prev.map(n => ({
                    ...n,
                    on: result.preferences[n.id] !== undefined ? result.preferences[n.id] : n.on
                  })));
                }
                closeModal();
              } catch (error) {
                // Error shown via apiMessage
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Reset to Defaults
          </button>
        </div>
      </div>
    );
  };

  // ==================== SELECTION ====================

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNotifs(filteredNotifs.map(n => n.id));
    } else {
      setSelectedNotifs([]);
    }
  };

  const toggleSelect = (id) => {
    if (selectedNotifs.includes(id)) {
      setSelectedNotifs(selectedNotifs.filter(n => n !== id));
    } else {
      setSelectedNotifs([...selectedNotifs, id]);
    }
  };

  // ==================== FILTER ====================

  const uniqueTypes = ['All', 'Unread', ...new Set(notifList.map(n => n.type))];
  
  const filteredNotifs = filter === 'All' 
    ? notifList 
    : filter === 'Unread' 
      ? notifList.filter(n => !n.read) 
      : notifList.filter(n => n.type === filter);

  const unreadCount = notifList.filter(n => !n.read).length;

  // ==================== RENDER ====================

  if (isLoadingPreferences && !isInitialLoad) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ marginBottom: '8px' }}><Loader2 size={20} /></div>
        <div style={{ color: 'var(--text-light)' }}>Loading notifications...</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          All platform activity and alerts in one place.
          {selectedNotifs.length > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
              · {selectedNotifs.length} selected
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedNotifs.length > 0 && (
            <>
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => {
                  setNotifList(notifList.map(n => 
                    selectedNotifs.includes(n.id) ? { ...n, read: true } : n
                  ));
                  setSelectedNotifs([]);
                  showToastMessage(`✅ ${selectedNotifs.length} notifications marked as read`, 'success');
                }}
                disabled={loading}
              >
                <Check size={16} style={{ verticalAlign: '-3px' }} /> Mark Read
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleBulkDelete}
                disabled={loading}
              >
                <Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Selected
              </button>
            </>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={markAllRead}
            disabled={loading}
          >
            <Check size={16} style={{ verticalAlign: '-3px' }} /> Mark all read
          </button>
        </div>
      </div>

      <div className="grid-65-35">
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              Notification Feed
              {unreadCount > 0 && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px', 
                  background: 'var(--gold)', 
                  color: 'var(--navy)',
                  padding: '1px 10px',
                  borderRadius: '12px',
                  fontWeight: '600'
                }}>
                  {unreadCount} unread
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select 
                className="form-select" 
                style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }} 
                value={filter} 
                onChange={(e) => {
                  setFilter(e.target.value);
                  setSelectedNotifs([]);
                }}
                disabled={loading}
              >
                {uniqueTypes.map(type => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          {filteredNotifs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-light)' }}>
              <div style={{ marginBottom: '12px' }}><Inbox size={48} strokeWidth={1.5} /></div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--navy)' }}>No notifications</div>
              <div style={{ fontSize: '13px', marginTop: '4px' }}>Try adjusting your filter</div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text-light)', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedNotifs.length === filteredNotifs.length && filteredNotifs.length > 0}
                    onChange={toggleSelectAll}
                    disabled={loading}
                  />
                  Select all
                </label>
              </div>
              {filteredNotifs.map(n => (
                <div 
                  key={n.id} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px', 
                    padding: '14px 0', 
                    borderBottom: '1px solid var(--border-light)', 
                    opacity: n.read ? 0.75 : 1,
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cream)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <input 
                    type="checkbox" 
                    checked={selectedNotifs.includes(n.id)}
                    onChange={() => toggleSelect(n.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ marginTop: '8px', cursor: 'pointer' }}
                    disabled={loading}
                  />
                  <div 
                    style={{ 
                      width: '38px', 
                      height: '38px', 
                      borderRadius: '10px', 
                      background: `${n.color}18`, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '18px', 
                      flexShrink: 0 
                    }}
                    onClick={() => handleViewDetails(n)}
                  >
                    <n.icon size={18} style={{ color: n.color }} />
                  </div>
                  <div style={{ flex: 1 }} onClick={() => handleViewDetails(n)}>
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: n.read ? '400' : '600', 
                      color: 'var(--navy)' 
                    }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-mid)', marginTop: '2px' }}>
                      {n.desc}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                      {n.time} · {n.type}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    {!n.read && (
                      <button 
                        className="btn btn-ghost btn-sm btn-icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(n);
                        }}
                        title="Mark as read"
                        disabled={loading}
                      >
                        <Check size={16} />
                      </button>
                    )}
                    <button 
                      className="btn btn-ghost btn-sm btn-icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNotification(n);
                      }}
                      title="Delete"
                      style={{ color: 'var(--red)' }}
                      disabled={loading}
                    >
                      <Trash2 size={16} />
                    </button>
                    {!n.read && (
                      <div style={{ 
                        width: '8px', 
                        height: '8px', 
                        borderRadius: '50%', 
                        background: 'var(--gold)', 
                        flexShrink: 0, 
                        marginTop: '5px' 
                      }} />
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header">
              <div className="card-title" style={{ marginBottom: '0' }}><Bell size={16} style={{ verticalAlign: '-3px' }} /> Notification Preferences</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleResetNotifications}
                  disabled={isSubmitting || isLoadingPreferences}
                >
                  <RefreshCw size={16} style={{ verticalAlign: '-3px' }} /> Reset
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={handleSaveNotifications}
                  disabled={isSubmitting || isLoadingPreferences}
                >
                  {isSubmitting ? <><Loader2 size={16} style={{ verticalAlign: '-3px' }} /> Saving...</> : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save</>}
                </button>
              </div>
            </div>
            {isLoadingPreferences ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-light)' }}>
                <div style={{ marginBottom: '8px' }}><Loader2 size={20} /></div>
                <div>Loading preferences...</div>
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  padding: '10px 0',
                  borderBottom: '1px solid var(--border-light)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ display: 'inline-flex' }}><n.icon size={20} /></span>
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--text)' }}>{n.label}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{n.desc}</div>
                    </div>
                  </div>
                  <label className="toggle">
                    <input 
                      type="checkbox" 
                      checked={n.on} 
                      onChange={() => toggleNotification(n.id)}
                      disabled={isSubmitting}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))
            )}
          </div>
          
          <div className="card">
            <div className="card-title" style={{ marginBottom: '12px' }}>Alert Summary</div>
            {[
              { label: 'Unread', value: unreadCount, color: 'var(--gold)' },
              { label: 'Today', value: notifList.filter(n => {
                const today = new Date();
                const notifDate = new Date(n.time);
                return notifDate.toDateString() === today.toDateString();
              }).length, color: 'var(--blue)' },
              { label: 'Total', value: notifList.length, color: 'var(--navy)' }
            ].map(s => (
              <div key={s.label} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '10px 0', 
                borderBottom: '1px solid var(--border-light)' 
              }}>
                <span style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{s.label}</span>
                <span style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  fontFamily: 'Playfair Display, serif', 
                  color: s.color 
                }}>
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .badge {
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .btn-danger {
          background: var(--red);
          color: white;
          border: none;
        }
        .btn-danger:hover {
          background: var(--red-dark);
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .grid-65-35 {
          display: grid;
          grid-template-columns: 65% 35%;
          gap: 20px;
        }
        .card-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--navy);
        }
        .toggle {
          position: relative;
          display: inline-block;
          width: 38px;
          height: 20px;
          flex-shrink: 0;
        }
        .toggle input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #ccc;
          transition: 0.3s;
          border-radius: 20px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 14px;
          width: 14px;
          left: 3px;
          bottom: 3px;
          background: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        .toggle input:checked + .toggle-slider {
          background: var(--gold);
        }
        .toggle input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }
        @media (max-width: 768px) {
          .grid-65-35 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
          }
        }
      `}</style>
    </>
  );
};

export default Notifications;